import { use, useEffect } from 'react';
import { useFormContext, UseFormReturn } from 'react-hook-form';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';
import { DeviceType } from 'shared/models/device-type';

import { useDeviceDetailsHook } from 'features/device/device-details/hooks/device-details-hook';
import Device from 'features/device/domain/models/device';
import GetLinkableGatewaysResponse from 'features/device/domain/models/get-linkable-gateways-response';
import GetLinkableSystemsResponse from 'features/device/domain/models/get-linkable-systems-response';
import { useGetLinkableGateways } from 'features/device/domain/queries/device-queries';

type DeviceDetailsPopupInformation = {
  currentViewingMode: ViewingMode;
  currentDevice?: Device;
  currentDeviceType?: DeviceType;
  gatewaysResponse?: GetLinkableGatewaysResponse;
  systemsResponse?: GetLinkableSystemsResponse;
  form: UseFormReturn;
  generateExternalId: () => void;
  showField: (field: string) => boolean;
};

const useDeviceDetailsPopupInformation = (): DeviceDetailsPopupInformation => {
  const { setErrorMessage } = use(ErrorMessageProviderContext);
  const { currentViewingMode, currentDevice, currentDeviceType, generateExternalId, systemsResponse } = useDeviceDetailsHook();

  const form = useFormContext();

  const { data: gatewaysResponse, error: gatewaysResponseError } = useGetLinkableGateways(
    currentDeviceType ?? '',
    currentDeviceType !== undefined,
  );

  const showField = (field: string): boolean => {
    if (currentViewingMode !== 'viewing') {
      return true;
    }

    const value = form.getValues(field)?.toString();
    return value && value.trim() !== '';
  };

  const watchGatewayId = form.watch('gatewayId');
  const watchDeviceType = form.watch('deviceType');
  const watchSystemId = form.watch('systemId');

  useEffect(() => {
    if (gatewaysResponseError) {
      setErrorMessage(gatewaysResponseError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatewaysResponseError]);

  useEffect(() => {
    if (currentViewingMode === 'creation' || currentViewingMode === 'editing') {
      const currentNameValue = form.getValues('name');
      if (currentNameValue && currentNameValue.length > 0) {
        form.trigger('name');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchGatewayId, watchDeviceType, currentViewingMode]);

  useEffect(() => {
    if (currentViewingMode === 'creation' || currentViewingMode === 'editing') {
      const currentDetectionLoopNumberValue = form.getValues('detectionLoopNumber');
      if (currentDetectionLoopNumberValue && currentDetectionLoopNumberValue.toString().length > 0) {
        form.trigger('detectionLoopNumber');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchSystemId, currentViewingMode]);

  return {
    currentDevice,
    currentViewingMode,
    currentDeviceType,
    gatewaysResponse,
    systemsResponse,
    form,
    generateExternalId,
    showField,
  };
};

export default useDeviceDetailsPopupInformation;
